/**
 * useAiLookup — React hook for triggering and tracking AI lookup results.
 *
 * Manages loading/error state for each lookup type independently.
 * Uses aiLookupService for HTTP calls.
 */

import { useState, useCallback } from 'react';
import {
  aiLookupService,
  AiPartsResult,
  AiRecallItem,
  AiTsbItem,
  AiFixHit,
  AiLicensedResult,
} from '../services/aiLookupService';

export interface AiLookupState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initialState<T>(): AiLookupState<T> {
  return { data: null, loading: false, error: null };
}

export interface UseAiLookupResult {
  parts: AiLookupState<AiPartsResult | unknown>;
  recalls: AiLookupState<AiRecallItem[]>;
  tsbs: AiLookupState<AiTsbItem[]>;
  similarFixes: AiLookupState<AiFixHit[]>;
  licensed: AiLookupState<AiLicensedResult>;
  licensedProvenance: string | null;
  lookupParts: (lineItemId: number, partQuery: string) => Promise<void>;
  lookupRecalls: (lineItemId: number) => Promise<void>;
  lookupTsbs: (lineItemId: number) => Promise<void>;
  lookupSimilarFixes: (lineItemId: number) => Promise<void>;
  lookupLicensed: (lineItemId: number, vendor: 'alldata' | 'autozone_pro', query: string) => Promise<void>;
}

export function useAiLookup(): UseAiLookupResult {
  const [parts, setParts] = useState<AiLookupState<AiPartsResult | unknown>>(initialState());
  const [recalls, setRecalls] = useState<AiLookupState<AiRecallItem[]>>(initialState());
  const [tsbs, setTsbs] = useState<AiLookupState<AiTsbItem[]>>(initialState());
  const [similarFixes, setSimilarFixes] = useState<AiLookupState<AiFixHit[]>>(initialState());
  const [licensed, setLicensed] = useState<AiLookupState<AiLicensedResult>>(initialState());
  const [licensedProvenance, setLicensedProvenance] = useState<string | null>(null);

  const lookupParts = useCallback(async (lineItemId: number, partQuery: string) => {
    setParts({ data: null, loading: true, error: null });
    try {
      const res = await aiLookupService.parts(lineItemId, partQuery);
      setParts({ data: res.result, loading: false, error: null });
    } catch (e: any) {
      setParts({ data: null, loading: false, error: e?.response?.data?.error ?? e.message ?? 'Parts lookup failed' });
    }
  }, []);

  const lookupRecalls = useCallback(async (lineItemId: number) => {
    setRecalls({ data: null, loading: true, error: null });
    try {
      const res = await aiLookupService.recalls(lineItemId);
      setRecalls({ data: res.result, loading: false, error: null });
    } catch (e: any) {
      setRecalls({ data: null, loading: false, error: e?.response?.data?.error ?? e.message ?? 'Recalls lookup failed' });
    }
  }, []);

  const lookupTsbs = useCallback(async (lineItemId: number) => {
    setTsbs({ data: null, loading: true, error: null });
    try {
      const res = await aiLookupService.tsbs(lineItemId);
      setTsbs({ data: res.result, loading: false, error: null });
    } catch (e: any) {
      setTsbs({ data: null, loading: false, error: e?.response?.data?.error ?? e.message ?? 'TSBs lookup failed' });
    }
  }, []);

  const lookupSimilarFixes = useCallback(async (lineItemId: number) => {
    setSimilarFixes({ data: null, loading: true, error: null });
    try {
      const res = await aiLookupService.similarFixes(lineItemId);
      setSimilarFixes({ data: res.result, loading: false, error: null });
    } catch (e: any) {
      setSimilarFixes({ data: null, loading: false, error: e?.response?.data?.error ?? e.message ?? 'Similar fixes lookup failed' });
    }
  }, []);

  const lookupLicensed = useCallback(
    async (lineItemId: number, vendor: 'alldata' | 'autozone_pro', query: string) => {
      setLicensed({ data: null, loading: true, error: null });
      setLicensedProvenance(null);
      try {
        const res = await aiLookupService.licensed(lineItemId, vendor, query);
        setLicensed({ data: res.result, loading: false, error: null });
        setLicensedProvenance(res.provenance ?? null);
      } catch (e: any) {
        setLicensed({
          data: null,
          loading: false,
          error: e?.response?.data?.error ?? e.message ?? 'Licensed lookup failed',
        });
      }
    },
    []
  );

  return {
    parts,
    recalls,
    tsbs,
    similarFixes,
    licensed,
    licensedProvenance,
    lookupParts,
    lookupRecalls,
    lookupTsbs,
    lookupSimilarFixes,
    lookupLicensed,
  };
}
